import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useMemo } from 'react';
import Button, { ButtonMode, ButtonSize } from '../Button';
import Icon, { ICONS } from '../Icon';
import Popup from '../Popup';
import Tooltip from '../Tooltip';
import TreeSelect, { getNodeById, TreeNodeModel } from '../TreeSelect';

import styles from './Filter.module.scss';

const cx = classNames.bind(styles);

const TITLES_COUNT_IN_TOOLTIP = 3;

interface FilterProps {
  data: TreeNodeModel[];
  placeholder: string;
  dataTestId?: string;
  onChange: (checkedKeys: string[]) => void;
  checkedKeys: string[];
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const getFilterKeys = (data: TreeNodeModel): string[] => {
  if (!data.children) {
    return [data.key];
  }
  const childrenTagIds = data.children.flatMap((node) => getFilterKeys(node));
  return [data.key, ...childrenTagIds];
};

const Filter = ({
  data,
  placeholder,
  dataTestId,
  onChange,
  checkedKeys,
  open,
  onOpen,
  onClose,
}: FilterProps): React.ReactElement => {
  const dataKeys = data.flatMap((filter) => getFilterKeys(filter));
  const validSelectedFilterKeys = Array.from(new Set(checkedKeys.filter((key) => dataKeys.includes(key))));

  const renderTooltip = () => {
    const titles = validSelectedFilterKeys
      .slice(0, TITLES_COUNT_IN_TOOLTIP)
      .map((key) => getNodeById(data, key)?.title);
    if (validSelectedFilterKeys.length > TITLES_COUNT_IN_TOOLTIP) {
      titles.push(`+${validSelectedFilterKeys.length - TITLES_COUNT_IN_TOOLTIP} more`);
    }
    return titles.map((title, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={`${title}-${i}`}>{title}</span>
    ));
  };

  const renderOption = (placeholder: string) => (
    <Tooltip
      disabled={!validSelectedFilterKeys.length}
      content={renderTooltip()}
      trigger={
        <span>
          {validSelectedFilterKeys.length !== 0 && (
            <span className={cx('count')} data-testid="filter-tree-selected-count">
              {validSelectedFilterKeys.length}
            </span>
          )}
          <span data-testid="filter-tree-selected-placeholder">{placeholder}</span>
        </span>
      }
    />
  );

  const buttonTriggerDisplayValue = useMemo(() => renderOption(placeholder), [validSelectedFilterKeys]);

  const renderTrigger = () => {
    return (
      <div className={cx('trigger')}>
        <Button
          className={cx('button-trigger')}
          mode={ButtonMode.SECONDARY}
          size={ButtonSize.SMALL}
          dataTestId="filter-button-trigger"
        >
          {buttonTriggerDisplayValue}
        </Button>
        <div className={cx('actions')}>
          {!isEmpty(validSelectedFilterKeys) && (
            <Button
              className={cx('remove-btn')}
              mode={ButtonMode.TERTIARY}
              onClick={(e) => {
                onChange([]);
                e.stopPropagation();
              }}
            >
              <Icon name={ICONS.Times} />
            </Button>
          )}
          <span className={cx('caret', { rotate: open })}>
            <Icon name={ICONS.CaretDown} height={10} width={10} />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={cx('body')} data-testid={dataTestId}>
      <Popup
        trigger={renderTrigger()}
        className={cx('dropdown-popup')}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        on="click"
        position="bottom left"
        size="mini"
      >
        <TreeSelect
          data={data}
          checkedKeys={validSelectedFilterKeys}
          setCheckedKeys={(validCheckedKeys) => {
            onChange(validCheckedKeys);
          }}
        />
      </Popup>
    </div>
  );
};

export default Filter;
